import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { notificationsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { 
  Search, 
  Menu, 
  User, 
  LogOut, 
  LayoutDashboard,
  Calendar,
  Globe,
  Home,
  Heart,
  Bell,
  FileText,
  HelpCircle,
  ChevronRight,
  MessageCircle,
  ShieldCheck,
  Phone
} from 'lucide-react';

const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data?.count || 0);
    } catch (e) { console.debug('Notification count fetch skipped:', e.message); }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
    window.location.href = '/prelanzamiento';
  };

  const isActive = (path) => location.pathname === path;

  const handleMenuNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Mobile bottom navigation
  const BottomNav = () => (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bottom-nav safe-bottom md:hidden">
      <div className="flex items-center justify-around py-2 px-4">
        <Link 
          to="/prelanzamiento" 
          className={`flex flex-col items-center gap-1 py-2 px-4 ${isActive('/prelanzamiento') ? 'text-primary' : 'text-gray-400'}`}
          data-testid="nav-home-mobile"
        >
          <Search className="w-6 h-6" />
          <span className="text-xs">{t('nav.explore')}</span>
        </Link>
        
        {isAuthenticated && user?.role !== 'creator' && (
          <Link 
            to="/my-activities" 
            className={`flex flex-col items-center gap-1 py-2 px-4 ${isActive('/my-activities') ? 'text-primary' : 'text-gray-400'}`}
            data-testid="nav-reservations-mobile"
          >
            <Calendar className="w-6 h-6" />
            <span className="text-xs">{t('nav.myActivities')}</span>
          </Link>
        )}

        {isAuthenticated && user?.role === 'creator' && !user?.is_admin && (
          <Link 
            to="/dashboard" 
            className={`flex flex-col items-center gap-1 py-2 px-4 ${isActive('/dashboard') ? 'text-primary' : 'text-gray-400'}`}
            data-testid="nav-dashboard-mobile"
          >
            <LayoutDashboard className="w-6 h-6" />
            <span className="text-xs">{t('nav.dashboard')}</span>
          </Link>
        )}

        {isAuthenticated && user?.is_admin && (
          <Link 
            to="/admin" 
            className={`flex flex-col items-center gap-1 py-2 px-4 ${isActive('/admin') ? 'text-primary' : 'text-gray-400'}`}
            data-testid="nav-admin-mobile"
          >
            <ShieldCheck className="w-6 h-6" />
            <span className="text-xs">Admin</span>
          </Link>
        )}

        {isAuthenticated && (
          <Link 
            to="/notifications" 
            className={`flex flex-col items-center gap-1 py-2 px-4 relative ${isActive('/notifications') ? 'text-primary' : 'text-gray-400'}`}
            data-testid="nav-notifications-mobile"
          >
            <Bell className="w-6 h-6" />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-2 w-2 h-2 bg-red-500 rounded-full" />
            )}
            <span className="text-xs">Alertas</span>
          </Link>
        )}

        {isAuthenticated ? (
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className={`flex flex-col items-center gap-1 py-2 px-4 ${mobileMenuOpen ? 'text-primary' : 'text-gray-400'}`}
            data-testid="nav-menu-mobile"
          >
            <Menu className="w-6 h-6" />
            <span className="text-xs">Menú</span>
          </button>
        ) : (
          <>
            <Link 
              to="/contact" 
              className={`flex flex-col items-center gap-1 py-2 px-4 ${isActive('/contact') ? 'text-primary' : 'text-gray-400'}`}
              data-testid="nav-contact-mobile"
            >
              <MessageCircle className="w-6 h-6" />
              <span className="text-xs">Contacto</span>
            </Link>
            <Link 
              to="/login" 
              className={`flex flex-col items-center gap-1 py-2 px-4 ${isActive('/login') ? 'text-primary' : 'text-gray-400'}`}
              data-testid="nav-login-mobile"
            >
              <User className="w-6 h-6" />
              <span className="text-xs">{t('nav.login')}</span>
            </Link>
          </>
        )}
      </div>
    </nav>
  );

  // Mobile menu sheet
  const MobileMenu = () => (
    <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
      <SheetContent side="right" className="w-[85%] max-w-[340px] rounded-l-2xl p-0 bg-white">
        <div className="flex flex-col h-full">
          {/* User header */}
          <div className="p-6 border-b border-gray-100">
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14">
                  <AvatarImage src={user?.profile_image} />
                  <AvatarFallback className="bg-primary text-white text-xl">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-rubik font-semibold text-lg">{user?.name}</p>
                  <p className="text-sm text-gray-400">{user?.email}</p>
                </div>
              </div>
            ) : (
              <div>
                <p className="font-rubik font-semibold text-xl mb-2">Inicia sesión</p>
                <p className="text-sm text-gray-400 mb-4">Accede a todas las funciones</p>
                <Button 
                  onClick={() => handleMenuNavigate('/login')}
                  className="w-full bg-primary hover:bg-primary-hover rounded-lg"
                >
                  {t('nav.login')}
                </Button>
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="flex-1 overflow-y-auto py-4">
            {isAuthenticated && (
              <>
                <MenuItem 
                  icon={User} 
                  label={t('nav.profile')} 
                  onClick={() => handleMenuNavigate('/profile')}
                />
                {user?.role !== 'creator' && (
                  <MenuItem 
                    icon={Calendar} 
                    label={t('nav.myActivities')} 
                    onClick={() => handleMenuNavigate('/my-activities')}
                  />
                )}
                <MenuItem 
                  icon={Bell} 
                  label={t('nav.alerts')} 
                  onClick={() => handleMenuNavigate('/notifications')}
                />
                <MenuItem 
                  icon={MessageCircle} 
                  label={t('nav.messages')} 
                  onClick={() => handleMenuNavigate('/messages')}
                />
                {user?.role === 'creator' && !user?.is_admin && (
                  <MenuItem 
                    icon={LayoutDashboard} 
                    label={t('nav.dashboard')} 
                    onClick={() => handleMenuNavigate('/dashboard')}
                  />
                )}
                {user?.is_admin && (
                  <MenuItem 
                    icon={ShieldCheck} 
                    label={t('admin.title')} 
                    onClick={() => handleMenuNavigate('/admin')}
                  />
                )}
                <div className="h-px bg-gray-100 my-4" />
              </>
            )}

            {/* Language toggle */}
            <MenuItem 
              icon={Globe} 
              label={`${t('nav.language')}: ${language === 'es' ? 'Español' : 'English'}`}
              onClick={toggleLanguage}
            />

            <div className="h-px bg-gray-100 my-4" />

            {/* Legal, Help & Contact */}
            <MenuItem 
              icon={FileText} 
              label={t('nav.legal')} 
              onClick={() => handleMenuNavigate('/legal')}
            />
            <MenuItem 
              icon={HelpCircle} 
              label={t('nav.help')} 
              onClick={() => handleMenuNavigate('/help')}
            />
            <MenuItem 
              icon={Phone} 
              label={t('nav.contact')} 
              onClick={() => handleMenuNavigate('/contact')}
            />

            {isAuthenticated && (
              <>
                <div className="h-px bg-gray-100 my-4" />
                <MenuItem 
                  icon={LogOut} 
                  label={t('nav.logout')} 
                  onClick={handleLogout}
                  className="text-red-500"
                />
              </>
            )}
          </div>

          {/* Footer copyright */}
          {!isAuthenticated && (
            <div className="px-6 py-4 border-t border-gray-100">
              <p className="text-xs text-gray-300 text-center">© {new Date().getFullYear()} utentik México</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );

  // Menu item component
  const MenuItem = ({ icon: Icon, label, onClick, className = '' }) => (
    <button 
      onClick={onClick}
      className={`w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors ${className}`}
    >
      <div className="flex items-center gap-4">
        <Icon className="w-5 h-5" />
        <span className="font-medium">{label}</span>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300" />
    </button>
  );

  return (
    <>
      {/* Desktop Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-11">
            {/* Logo */}
            <Link to="/prelanzamiento" className="flex items-center gap-2" data-testid="logo-link" onClick={() => window.dispatchEvent(new CustomEvent('utentik:clear-search'))}>
              <img src="/utentik-logo.png" alt="utentik" className="h-4" />
            </Link>

            {/* Desktop Navigation - removed per user request */}

            {/* Right side */}
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-gray-400 hover:text-gray-500 rounded-full gap-1 h-8 px-2"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4" />
                <span className="text-[11px] font-medium">{language === 'es' ? 'ES' : 'EN'}</span>
              </Button>

              {/* Notification Bell */}
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/notifications')}
                  className="text-gray-400 hover:text-gray-500 rounded-full relative h-8 w-8 p-0"
                  data-testid="notification-bell-desktop"
                >
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
                  )}
                </Button>
              )}

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex items-center gap-1.5 rounded-full border-gray-200 px-2 py-1 h-auto hover:shadow-md transition-shadow"
                      data-testid="user-menu-trigger"
                    >
                      <Menu className="w-3.5 h-3.5" />
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={user?.profile_image} alt={user?.name} />
                        <AvatarFallback className="bg-primary text-white text-[10px]">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 rounded-xl p-2">
                    <div className="px-3 py-2 mb-2">
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-sm text-gray-400">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} className="py-3 rounded-lg" data-testid="menu-profile">
                      <User className="w-4 h-4 mr-3" />
                      {t('nav.profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/notifications')} className="py-3 rounded-lg" data-testid="menu-notifications">
                      <Bell className="w-4 h-4 mr-3" />
                      {t('nav.alerts')}
                      {unreadCount > 0 && (
                        <span className="ml-auto text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center">{unreadCount}</span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} className="py-3 rounded-lg" data-testid="menu-messages">
                      <MessageCircle className="w-4 h-4 mr-3" />
                      {t('nav.messages')}
                    </DropdownMenuItem>
                    {user?.role !== 'creator' && (
                      <DropdownMenuItem onClick={() => navigate('/my-activities')} className="py-3 rounded-lg" data-testid="menu-my-activities">
                        <Calendar className="w-4 h-4 mr-3" />
                        {t('nav.myActivities')}
                      </DropdownMenuItem>
                    )}
                    {user?.role === 'creator' && !user?.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} className="py-3 rounded-lg" data-testid="menu-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-3" />
                        {t('nav.dashboard')}
                      </DropdownMenuItem>
                    )}
                    {user?.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="py-3 rounded-lg" data-testid="menu-admin">
                        <ShieldCheck className="w-4 h-4 mr-3" />
                        {t('admin.title')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="py-3 rounded-lg text-red-500" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-3" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className="rounded-full"
                    data-testid="login-btn"
                  >
                    {t('nav.login')}
                  </Button>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-primary hover:bg-primary-hover text-white rounded-full"
                    data-testid="register-btn"
                  >
                    {t('nav.register')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header - simpler */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white safe-top md:hidden">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/prelanzamiento" data-testid="logo-link-mobile" onClick={() => window.dispatchEvent(new CustomEvent('utentik:clear-search'))}>
            <img src="/utentik-logo.png" alt="utentik" className="h-5" />
          </Link>
          <div className="flex items-center gap-1">
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <BottomNav />

      {/* Mobile menu sheet */}
      <MobileMenu />
    </>
  );
};

export default Header;
